import { useCallback, useEffect, useMemo, useState } from 'react';
import { EVENT_META, type BabyEvent, type EventType, type MeDto } from '@bb/shared/core';
import type { RecentDto } from './api';
import { api, ApiError } from './api';
import { platform } from './platform';
import { loadThemeMode, resolveTheme, saveThemeMode, type ThemeMode } from './lib';
import { Nav, Toast, type Tab, type ToastData } from './components';
import { Ctx, type AppCtx, type EditorTarget } from './state';
import { Onboarding } from './screens/Onboarding';
import { Today } from './screens/Today';
import { Diary } from './screens/Diary';
import { AddSheet } from './screens/AddSheet';
import { EventEditor } from './screens/EventEditor';
import { Settings } from './screens/Settings';
import { Soon } from './screens/Soon';
import { Progress } from './screens/Progress';
import { ActionSheet } from './screens/ActionSheet';
import { AlarmWatcher, TimerBar } from './screens/TimerBar';

type Boot = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; me: MeDto };

export function App() {
  const [boot, setBoot] = useState<Boot>({ state: 'loading' });
  const [tab, setTab] = useState<Tab>('today');
  const [version, setVersion] = useState(0);
  const [toastData, setToastData] = useState<ToastData | null>(null);
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [themeMode, setThemeModeState] = useState<ThemeMode>(loadThemeMode);
  const [themeTick, setThemeTick] = useState(0);
  const [childId, setChildId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('childId');
    } catch {
      return null;
    }
  });
  const [diaryDate, setDiaryDate] = useState<{ date: string; n: number } | null>(null);
  const [action, setAction] = useState<{ type: EventType; event?: BabyEvent } | null>(null);
  const [recent, setRecent] = useState<RecentDto | null>(null);
  const [recentTick, setRecentTick] = useState(0);

  /* Тема: применяем к <html>, «Авто» пересчитываем раз в минуту и при смене темы мессенджера. */
  useEffect(() => {
    document.documentElement.dataset.theme = resolveTheme(themeMode);
  }, [themeMode, themeTick]);
  useEffect(() => {
    const t = setInterval(() => setThemeTick((n) => n + 1), 60000);
    const off = platform.onThemeChange(() => setThemeTick((n) => n + 1));
    return () => {
      clearInterval(t);
      off();
    };
  }, []);

  const loadMe = useCallback(async () => {
    try {
      let me = await api.me();
      const start = me.startParam ?? platform.startParam;
      if (start?.startsWith('inv_')) {
        try {
          const res = await api.acceptInvite(start.slice(4));
          if (res.joined) me = await api.me();
        } catch (e) {
          // Приглашение могло истечь — покажем, но не блокируем вход.
          const msg = e instanceof ApiError ? e.message : 'Приглашение не сработало';
          setToastData({ id: Date.now(), text: msg });
        }
      }
      setBoot({ state: 'ready', me });
    } catch (e) {
      setBoot({ state: 'error', message: e instanceof ApiError ? e.message : 'Не удалось загрузиться' });
    }
  }, []);

  useEffect(() => {
    platform.ready();
    void loadMe();
  }, [loadMe]);

  /* Системная кнопка «Назад»: закрывает верхний слой. */
  useEffect(() => {
    if (action) platform.setBack(() => setAction(null));
    else if (editor) platform.setBack(() => setEditor(null));
    else if (addOpen) platform.setBack(() => setAddOpen(false));
    else if (settingsOpen) platform.setBack(() => setSettingsOpen(false));
    else if (tab !== 'today') platform.setBack(() => setTab('today'));
    else platform.setBack(null);
  }, [action, editor, addOpen, settingsOpen, tab]);

  const reloadMe = useCallback(async () => {
    const me = await api.me();
    setBoot({ state: 'ready', me });
  }, []);

  const toast = useCallback((t: Omit<ToastData, 'id'>) => setToastData({ ...t, id: Date.now() }), []);
  const fail = useCallback(
    (e: unknown) => {
      platform.haptic('error');
      toast({ text: e instanceof ApiError ? e.message : 'Что-то пошло не так' });
    },
    [toast],
  );

  const me = boot.state === 'ready' ? boot.me : null;
  const family = me?.families[0] ?? null;
  const child = family?.children.find((c) => c.id === childId) ?? family?.children[0] ?? null;

  /* Последние 48 часов: для «Сегодня», таймеров на всех экранах и подсказок. */
  const childKey = child?.id ?? null;
  useEffect(() => {
    // Изменения с других телефонов семьи подтягиваются раз в 15 секунд (и сразу при возврате в приложение).
    const t = setInterval(() => setRecentTick((n) => n + 1), 15000);
    const onVisible = () => document.visibilityState === 'visible' && setRecentTick((n) => n + 1);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  useEffect(() => {
    if (!childKey) return;
    let alive = true;
    api.recent(childKey).then(
      (r) => alive && setRecent(r),
      () => {},
    );
    return () => {
      alive = false;
    };
  }, [childKey, version, recentTick]);
  const recentForChild = recent && recent.events.every((e) => e.childId === childKey) ? recent : null;
  const running = useMemo(() => (recentForChild?.events ?? []).filter((e) => e.endedAt === null && EVENT_META[e.type].timed), [recentForChild]);

  const ctx = useMemo<AppCtx | null>(() => {
    if (!me || !family || !child) return null;
    return {
      me,
      family,
      child,
      version,
      bump: () => setVersion((v) => v + 1),
      reloadMe,
      toast,
      fail,
      openEditor: (t) => {
        setAddOpen(false);
        setEditor(t);
      },
      openAdd: () => setAddOpen(true),
      openSettings: () => setSettingsOpen(true),
      selectChild: (id) => {
        try {
          localStorage.setItem('childId', id);
        } catch {
          /* нет хранилища */
        }
        setChildId(id);
      },
      openDiary: (date) => {
        setSettingsOpen(false);
        setDiaryDate((d) => ({ date, n: (d?.n ?? 0) + 1 }));
        setTab('diary');
      },
      openProgress: () => {
        setSettingsOpen(false);
        setTab('progress');
      },
      recent: recentForChild,
      running,
      openAction: (type, event) => {
        setAddOpen(false);
        setAction({ type, event });
      },
      themeMode,
      setThemeMode: (m) => {
        saveThemeMode(m);
        setThemeModeState(m);
      },
    };
  }, [me, family, child, version, reloadMe, toast, fail, themeMode, recentForChild, running]);

  if (boot.state === 'loading') {
    return (
      <div className="app stack" aria-busy="true">
        <div className="skeleton" style={{ minHeight: 52 }} />
        <div className="skeleton" style={{ minHeight: 160 }} />
        <div className="skeleton" />
      </div>
    );
  }
  if (boot.state === 'error') {
    return (
      <div className="app center">
        <h1 className="h2">Не получилось открыть дневник</h1>
        <p className="muted">{boot.message}</p>
        <button type="button" className="btn btn-primary" onClick={() => void loadMe()}>
          Попробовать ещё раз
        </button>
      </div>
    );
  }
  if (!ctx) {
    return (
      <>
        <Onboarding me={boot.me} onDone={reloadMe} />
        <Toast toast={toastData} onDone={() => setToastData(null)} />
      </>
    );
  }

  return (
    <Ctx.Provider value={ctx}>
      <main className="app">
        {(settingsOpen || tab !== 'today') && ctx.running.length > 0 && (
          <div className="sticky-top">
            <TimerBar />
          </div>
        )}
        {settingsOpen ? (
          <Settings onClose={() => setSettingsOpen(false)} />
        ) : tab === 'today' ? (
          <Today />
        ) : tab === 'diary' ? (
          <Diary key={`${child?.id}-${diaryDate?.n ?? 0}`} initialDate={diaryDate?.date} />
        ) : tab === 'progress' ? (
          <Progress key={child?.id} />
        ) : (
          <Soon title="Забота" text="Питание мамы, одежда по погоде, гимнастика и советы — в следующем обновлении." />
        )}
      </main>
      <Nav
        tab={tab}
        onTab={(t) => {
          setSettingsOpen(false);
          setTab(t);
        }}
        onPlus={() => setAddOpen(true)}
      />
      {addOpen && <AddSheet onClose={() => setAddOpen(false)} />}
      {editor && <EventEditor target={editor} onClose={() => setEditor(null)} />}
      {action && <ActionSheet type={action.type} event={action.event} onClose={() => setAction(null)} />}
      <AlarmWatcher />
      <Toast toast={toastData} onDone={() => setToastData(null)} />
    </Ctx.Provider>
  );
}
